/* eslint-disable react-hooks/exhaustive-deps */
import { FormEvent, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { login } from '../../api/login';
import {
  AuthCard,
  AuthCardHeader,
  AuthShell,
  CardFooter,
  Field,
  GhostLink,
  Input,
  SubmitButton,
} from '../../components/AuthShell';
import { PasswordToggle } from '../../components/AuthShell/PasswordToggle';
import Snackbar from '../../components/Snackbar';
import { WhiteCircularProgress } from '../../components/UI/Typography';
import { Constants } from '../../constants/login';
import { GlobalContext } from '../../context/global';
import { getCompanySubdomain } from '../../helpers';
import {
  getLocalStorageToken,
  getMfaPendingSession,
} from '../../helpers/localStorage';
import { openSessionFromLogin } from '../../helpers/session';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';

interface LoginProps {
  /** Tenant (bank/company) logo from `login-layout`; omitted for the default brand. */
  tenantLogo?: string;
}

export function Login({ tenantLogo }: LoginProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const companySubdomain = getCompanySubdomain();

  const { openSnackbar } = useContext(GlobalContext);
  const { handleSnackbar } = useSnackbar();

  const { getErrorMessage } = useErrorMessage();

  const navigate = useNavigate();
  const token = getLocalStorageToken();

  useEffect(() => {
    // A login left half-done resumes on the MFA screen, not on /home: the
    // pending token is refused everywhere else (REAL-3266).
    if (getMfaPendingSession()) {
      navigate('/mfa');
      return;
    }
    if (token !== null && token !== 'undefined') {
      navigate('/home');
    }
  }, [navigate, token]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimEmail = email.trim();

    try {
      setSubmitLoading(true);
      const response = await login({
        email: trimEmail,
        password,
        companySubdomain,
      });
      navigate(openSessionFromLogin(response));
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthCard id="login" onSubmit={handleSubmit} noValidate>
        <AuthCardHeader
          title={Constants.title}
          subtitle={Constants.subtitle}
          tenantLogo={tenantLogo}
        />

        <Field>
          <label htmlFor="email">{Constants.emailLabel}</label>
          <Input
            required
            id="email"
            type="email"
            autoComplete="username"
            placeholder={Constants.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
        </Field>

        <Field>
          <label htmlFor="password">{Constants.passwordLabel}</label>
          <Input
            required
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder={Constants.passwordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: (
                <PasswordToggle
                  visible={showPassword}
                  onToggle={() => setShowPassword((value) => !value)}
                  showLabel={Constants.showPassword}
                  hideLabel={Constants.hidePassword}
                />
              ),
            }}
          />
        </Field>

        <SubmitButton
          form="login"
          type="submit"
          size="large"
          disabled={submitLoading}
        >
          {submitLoading ? (
            <WhiteCircularProgress size={18} sx={{ color: 'inherit' }} />
          ) : (
            Constants.submit
          )}
        </SubmitButton>

        <GhostLink
          disableTouchRipple
          onClick={() => navigate('/forgot-password')}
        >
          {Constants.forgotPassword}
        </GhostLink>

        <CardFooter>{Constants.footer}</CardFooter>
      </AuthCard>
      {openSnackbar && <Snackbar />}
    </AuthShell>
  );
}
